import Cookies from "js-cookie";
import type { ReactNode } from "react";
import {
	createContext,
	createElement,
	use,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSession } from "@/hooks/auth-hooks";
import { authClient } from "@/lib/auth-client";
import { db } from "@/lib/instantdb";
import {
	normalizeSubscriptionPlan,
	type SubscriptionTier,
	selectBestActiveSubscription,
} from "@/lib/plans";

export interface Subscription {
	plan: string;
	status: string;
	periodEnd?: Date;
	cancelAtPeriodEnd?: boolean;
	stripeSubscriptionId?: string;
}

interface SubscriptionContextValue {
	subscription: Subscription | null;
	isLoading: boolean;
	isActive: boolean;
	tier: SubscriptionTier;
	isPaid: boolean;
	refetch: () => Promise<void>;
}

const PLAN_COOKIE_NAME = "lw_plan";

function getPlanFromCookie(): SubscriptionTier {
	if (typeof document === "undefined") return "free";
	const value = Cookies.get(PLAN_COOKIE_NAME);
	if (value === "pro" || value === "max") return value;
	return "free";
}

function setPlanCookie(tier: SubscriptionTier): void {
	if (typeof document === "undefined") return;
	Cookies.set(PLAN_COOKIE_NAME, tier, {
		expires: 30,
		sameSite: "lax",
		path: "/",
	});
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(
	null,
);

async function fetchActiveSubscription(
	userId: string,
): Promise<Subscription | null> {
	type SubscriptionCandidate = {
		status?: string;
		plan?: string;
		periodEnd?: string | Date;
		cancelAtPeriodEnd?: boolean;
		stripeSubscriptionId?: string;
	};
	const candidates: SubscriptionCandidate[] = [];

	// Stripe-backed subscriptions from Better Auth.
	try {
		const result = await authClient.subscription.list();
		if (result.data?.length) {
			candidates.push(...(result.data as SubscriptionCandidate[]));
		}
	} catch {
		// Ignore and continue with InstantDB fallback
	}

	// Also include InstantDB subscriptions (admin-granted/gifted plans)
	try {
		const result = await db.queryOnce({
			subscriptions: { $: { where: { referenceId: userId } } },
		});
		const subscriptions = result.data?.subscriptions as
			| SubscriptionCandidate[]
			| undefined;
		if (subscriptions?.length) {
			candidates.push(...subscriptions);
		}
	} catch {
		// Ignore and rely on other source
	}

	const activeSub = selectBestActiveSubscription(candidates);
	if (activeSub) {
		return {
			plan: activeSub.plan || "pro",
			status: activeSub.status || "active",
			periodEnd: activeSub.periodEnd
				? new Date(activeSub.periodEnd)
				: undefined,
			cancelAtPeriodEnd: activeSub.cancelAtPeriodEnd,
			stripeSubscriptionId: activeSub.stripeSubscriptionId ?? undefined,
		};
	}

	return null;
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
	const { session, user } = useSession();
	const userId = user?.id ?? session?.userId ?? null;

	const [subscription, setSubscription] = useState<Subscription | null>(() => {
		const tier = getPlanFromCookie();
		return tier !== "free" ? { plan: tier, status: "active" } : null;
	});
	const [isLoading, setIsLoading] = useState(true);
	const [refreshToken, setRefreshToken] = useState(0);

	const requestIdRef = useRef(0);
	const lastFetchedKeyRef = useRef<string | null>(null);

	const refetch = useCallback(async () => {
		if (!userId) return;
		setRefreshToken((v) => v + 1);
	}, [userId]);

	useEffect(() => {
		// Logged out: reset quickly
		if (!userId) {
			setSubscription(null);
			setPlanCookie("free");
			requestIdRef.current += 1;
			lastFetchedKeyRef.current = null;
			setIsLoading(false);
			return;
		}

		const fetchKey = `${userId}:${refreshToken}`;
		if (lastFetchedKeyRef.current === fetchKey) return;

		requestIdRef.current += 1;
		const requestId = requestIdRef.current;
		setIsLoading(true);

		(async () => {
			try {
				const sub = await fetchActiveSubscription(userId);
				if (requestId !== requestIdRef.current) return;

				setSubscription(sub);
				setPlanCookie(normalizeSubscriptionPlan(sub?.plan).tier);
				lastFetchedKeyRef.current = fetchKey;
			} catch {
				if (requestId !== requestIdRef.current) return;
				setSubscription(null);
				setPlanCookie("free");
				lastFetchedKeyRef.current = fetchKey;
			} finally {
				if (requestId === requestIdRef.current) {
					setIsLoading(false);
				}
			}
		})();
	}, [userId, refreshToken]);

	const isActive = subscription?.status === "active";
	const tier = normalizeSubscriptionPlan(subscription?.plan).tier;
	const isPaid = !!isActive && tier !== "free";

	const value = useMemo<SubscriptionContextValue>(
		() => ({
			subscription,
			isLoading,
			isActive: !!isActive,
			tier,
			isPaid,
			refetch,
		}),
		[subscription, isLoading, isActive, tier, isPaid, refetch],
	);

	return createElement(SubscriptionContext.Provider, { value }, children);
}

// Hook for components - userId param is kept for backwards compatibility but ignored
export function useSubscription(_userId?: string): SubscriptionContextValue {
	const context = use(SubscriptionContext);

	if (!context) {
		const tier = getPlanFromCookie();
		const subscription =
			tier !== "free" ? { plan: tier, status: "active" } : null;
		const isActive = subscription?.status === "active";

		return {
			subscription,
			isLoading: false,
			isActive: !!isActive,
			tier,
			isPaid: !!isActive && tier !== "free",
			refetch: async () => {},
		};
	}

	return context;
}
